import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, ButtonBase, Skeleton, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import { AppAntSwitch, AppBrandMark, AppEmptyLine, AppInfoButton, AppNote, secondaryButtonSx } from '@components'
import { dsColorNew } from '@palette'
import { CurveChart, Tooltip, tipText } from '../../../@components/PriceCurve'
import { curveFoot, curveSubtitle, curveTooltip, DEFAULT_LINES, linesState, notCapturedZone, touchLines } from '../../../@services/curve'
import { fmtWeekday, fromIso } from '../../../@services/dates'
import { money, signedPct } from '../../../@services/format'
import { cardSx } from './Cards'


const HEIGHT = 340

/**
 * "Price curve before pick-up" (CU-869fmjzrg UX § 6): every provider shown as a line, You and Market median on top of
 * the legend; hover a name to highlight its line, click it to hide it; last year dotted for You, the median and the
 * highlighted line (UX D3); click a day (or Enter after ← →) to see its prices; a ring on the move that came first.
 */
export default function DateCurve({ curve, curveId = null, loading, error, onRetry, stat, lor, code, today, ly, onLy, ring, firstMove = null, scope, onDay, onWeekly, onExplorer }) {
	// Curro's review: with many providers the curve opens with You, the median and the 5 nearest to your price (the 5
	// cheapest without you); the others start off and turn on from the legend. Every new curve opens with its defaults,
	// unless the user turned chips for this same answer (QA: `linesState`)
	const [lines, setLines] = useState(() => linesState(null, curve, { dataId: curveId, stat }))
	const current = linesState(lines, curve, { dataId: curveId, stat })
	useEffect(() => { if (current !== lines) setLines(current) }, [current])
	const hidden = current.hidden
	const setHidden = next => setLines(s => touchLines(linesState(s, curve, { dataId: curveId, stat }), typeof next === 'function' ? next(hidden) : next))
	const resetLines = () => setLines(linesState(null, curve, { dataId: curveId, stat }))
	const [highlight, setHighlight] = useState(null)
	const [hover, setHover] = useState(null)

	const lastValue = l => [...l.points].sort((a, b) => a.d - b.d)[0]?.value ?? null
	const legend = useMemo(() => {
		if (!curve) return []
		const top = curve.lines.filter(l => l.role !== 'other')
		// the default lines first, then the others (greyed while off), each group by today's price; a stable order, so a
		// chip never moves under the pointer when it is clicked
		const first = new Set(current.first)
		const byPrice = (a, b) => (lastValue(a) ?? Infinity) - (lastValue(b) ?? Infinity)
		const rest = curve.others.slice().sort((a, b) => (first.has(a.key) ? 0 : 1) - (first.has(b.key) ? 0 : 1) || byPrice(a, b))
		return [...top, ...rest]
	}, [curve, current.first.join(',')])
	const chartCurve = useMemo(() => {
		if (!curve) return null
		const hl = curve.others.find(l => l.key === highlight)
		const main = [...(curve.you ? [curve.you] : []), curve.median, ...(hl ? [hl] : [])]
		return { days: curve.days, single: curve.single, pickupDates: curve.pickupDates, hasPrev: curve.hasPrev, main, others: curve.others.filter(l => l !== hl) }
	}, [curve, highlight])
	const zone = useMemo(() => (curve ? notCapturedZone({ pickupDates: curve.pickupDates, days: curve.days, today }) : null), [curve, today])
	// CA 11, as the mock: a dashed vertical mark at the first move, in that line's colour, "<provider> moved first −5.9%"
	const marker = useMemo(() => {
		const line = firstMove && curve?.lines.find(l => l.key === firstMove.key)
		return line && !hidden[line.key] ? { d: firstMove.d, label: `${line.name} moved first ${signedPct(firstMove.pct)}`, color: line.color } : null
	}, [firstMove?.key, firstMove?.d, curve, hidden])

	const header = <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
		<Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<Typography sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle }}>Price curve before pick-up</Typography>
				<AppInfoButton title="How to read this curve" tooltip="How to read this curve">
					<p><b>Each point</b> is a provider's price (Min, Avg or Max of the prices captured that day) for this pick-up, by days before it. By week, each point joins the week's pick-ups at that distance.</p>
					<p><b>Market median</b> is the median of the competitors shown that had a price that day; when one of them has no price, the median can jump.</p>
					<p>This curve covers the <b>60 days</b> before pick-up; the curve inside a cell of Season vs last year covers 45. Within those 45 days they give the same numbers.</p>
					<p><b>Hollow points</b>: fewer than 5 prices that day.</p>
				</AppInfoButton>
			</Box>
			{!!curve && <Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.25 }}>
				{curveSubtitle({ pickupDates: curve.pickupDates, stat, lor })}{scope ? <> · {scope}</> : null}
			</Typography>}
		</Box>
		<Button variant="text" size="small" onClick={onWeekly} sx={{ textTransform: 'none' }}>See it week by week</Button>
	</Box>

	if (loading && !curve) return <Box component="section" aria-label="Price curve" sx={cardSx}>
		{header}
		<Skeleton variant="rounded" width="100%" height={HEIGHT} aria-busy="true" sx={{ mt: 1.25, borderRadius: '10px', transform: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', visibility: 'visible' }}>
			<Typography sx={{ visibility: 'visible', fontSize: 13, color: dsColorNew.textMuted }}>Loading the price curve…</Typography>
		</Skeleton>
	</Box>

	if (error && !curve) return <Box component="section" aria-label="Price curve" sx={cardSx}>
		{header}
		<AppNote severity="error" sx={{ mt: 1.25 }}>
			<b>Could not load the prices.</b> Your view is kept.
			<Box sx={{ mt: 1 }}><Button variant="outlined" onClick={onRetry} sx={secondaryButtonSx}>Try again</Button></Box>
		</AppNote>
	</Box>

	if (curve?.beyond) {
		const starts = fromIso(curve.startsOn)
		return <Box component="section" aria-label="Price curve" sx={cardSx}>
			{header}
			<Box sx={{ py: 4, textAlign: 'center' }}>
				<Typography sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle }}>The curve starts {curve.days} days before pick-up</Typography>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 0.5 }}>
					…{starts ? ` starts on ${fmtWeekday(starts)}` : ''}. Until then, see how prices are moving week by week.
				</Typography>
				<Button variant="outlined" onClick={onWeekly} sx={{ ...secondaryButtonSx, mt: 1.5 }}>See it week by week</Button>
			</Box>
		</Box>
	}

	if (!curve || curve.empty) return <Box component="section" aria-label="Price curve" sx={cardSx}>
		{header}
		<Box sx={{ py: 4, textAlign: 'center' }}>
			<Typography sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle }}>No prices for this pick-up date</Typography>
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
				<Button variant="text" onClick={onExplorer} sx={{ textTransform: 'none', color: dsColorNew.textMuted }}>Open in Advanced (BI)</Button>
			</Box>
		</Box>
	</Box>

	const onlyTwo = curve.others.length > 0 && curve.others.every(l => hidden[l.key])
	const allShown = curve.others.every(l => !hidden[l.key])
	const shownCount = curve.others.filter(l => !hidden[l.key]).length
	const visibleAny = curve.lines.some(l => !hidden[l.key])
	const tip = hover ? curveTooltip(chartCurve, hover.d, { hidden, showPrev: ly, showOthers: true }) : null
	if (tip) tip.rows = tip.rows.slice().sort((a, b) => (a.value ?? Infinity) - (b.value ?? Infinity))
	const foot = curveFoot(chartCurve, { hidden, showPrev: ly })
	const label = `Price curve of ${legend.filter(l => !hidden[l.key]).map(l => l.name).join(', ')}, by days before pick-up. Use the left and right arrows to move day by day and Enter to see that day's prices.`

	return <Box component="section" aria-label="Price curve" sx={cardSx}>
		{header}
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 1.25, mb: 0.5 }}>
			{legend.map(l => {
				const v = lastValue(l)
				return <ButtonBase key={l.key} aria-pressed={!hidden[l.key]}
					onClick={() => setHidden(h => ({ ...h, [l.key]: !h[l.key] }))}
					onMouseEnter={() => setHighlight(l.role === 'other' ? l.key : null)} onMouseLeave={() => setHighlight(null)}
					onFocus={() => setHighlight(l.role === 'other' ? l.key : null)} onBlur={() => setHighlight(null)}
					sx={{
						display: 'inline-flex', alignItems: 'center', gap: 0.6, height: 24, px: 0.9, borderRadius: '8px', border: `1px solid ${dsColorNew.grayBorder}`,
						fontSize: 12, color: hidden[l.key] ? dsColorNew.textSubtle : dsColorNew.textDark, backgroundColor: hidden[l.key] ? dsColorNew.pillTrackBg : dsColorNew.white,
						fontWeight: l.role === 'other' ? 400 : 600, '&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.webBlue500}` },
					}}>
					<Box component="i" sx={{ width: 14, height: l.role === 'you' ? 4 : 3, borderRadius: '2px', display: 'inline-block', backgroundColor: l.color, opacity: hidden[l.key] ? 0.35 : 1 }}/>
					{l.providerId !== undefined && <AppBrandMark name={l.name} size={14}/>}
					{l.name}
					{!!l.tag && <Box component="span" sx={{ px: 0.75, borderRadius: '999px', fontSize: 11, fontWeight: 600, backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}>{l.tag}</Box>}
					{l.role === 'other' && v !== null && <Box component="span" sx={{ color: dsColorNew.textSubtle }}>{money(v, code)}</Box>}
				</ButtonBase>
			})}
			{!onlyTwo && <Button variant="text" size="small" sx={{ textTransform: 'none' }}
				onClick={() => setHidden(Object.fromEntries(curve.others.map(l => [l.key, true])))}>Only you and the median</Button>}
			{curve.others.length > DEFAULT_LINES && <Button variant="text" size="small" sx={{ textTransform: 'none' }} onClick={resetLines}>
				{curve.you ? `The ${DEFAULT_LINES} nearest to you` : `The ${DEFAULT_LINES} cheapest`}</Button>}
			{!allShown && <Button variant="text" size="small" sx={{ textTransform: 'none' }} onClick={() => setHidden({})}>Show all ({curve.others.length})</Button>}
			<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>{shownCount} of {curve.others.length} shown</Typography>
			<Box sx={{ flex: 1 }}/>
			{curve.hasPrev && <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
				<AppAntSwitch checked={ly} onChange={onLy} inputProps={{ role: 'switch', 'aria-label': 'Last year (dotted)' }}/>
				<Typography sx={{ fontSize: 12.5, color: dsColorNew.textDark, cursor: 'pointer' }} onClick={onLy}>Last year <Box component="span" sx={{ color: dsColorNew.textSubtle }}>(dotted)</Box></Typography>
			</Box>}
		</Box>

		{!visibleAny
			? <Box sx={{ py: 3 }}><AppEmptyLine>Every line is hidden. Click a name above to show it.</AppEmptyLine></Box>
			: <Box sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
				<CurveChart curve={chartCurve} hidden={hidden} showPrev={ly} showOthers code={code} marker={marker} zone={zone}
					hover={hover} onHover={setHover} label={label} height={HEIGHT} allBullets endLabels highlight={highlight}
					onDayClick={onDay} ring={ring}/>
				{!!tip && <Tooltip tip={tip} x={hover.x} code={code} footer="Click to see this day's prices"/>}
				<Box aria-live="polite" sx={visuallyHidden}>{tip ? tipText(tip, code) : ''}</Box>
			</Box>}

		<Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
			{!!foot.thin && <span>{foot.thin}</span>}
			<span>{foot.gaps}</span>
			<span>Click a day to see its prices.</span>
		</Box>
	</Box>
}
